import React from 'react'
import { BulkActionDialog, BulkActionDialogActions } from './BulkActionDialog'
import { BulkActionStatus } from './BulkActionStatus'

export interface OutOfSyncReviewViewProps {
    isOpen: boolean
    actionDescription: string
    conflictCount: number
    selectedCount: number
    loading: boolean
    children: React.ReactNode
    onClose: () => void
    onRetry: () => void
}

export const OutOfSyncReviewView = ({
    isOpen,
    actionDescription,
    conflictCount,
    selectedCount,
    loading,
    children,
    onClose,
    onRetry,
}: OutOfSyncReviewViewProps): JSX.Element => (
    <BulkActionDialog
        isOpen={isOpen}
        onClose={onClose}
        width="wide"
        title="Review out-of-sync items"
        description="Amazon data changed after this bulk action was configured. No updates have been made yet."
        dismissible={!loading}
        footer={
            <BulkActionDialogActions
                primaryLabel="Retry update"
                loadingLabel="Retrying…"
                loading={loading}
                disabled={selectedCount === 0}
                onPrimary={onRetry}
                onCancel={onClose}
            />
        }
    >
        <BulkActionStatus
            phase={loading ? 'running' : 'conflict'}
            title={
                loading ? 'Starting the bulk action again' : `${conflictCount} items need review`
            }
            message={
                loading ? (
                    'Waiting for the bulk-action service to acknowledge the update.'
                ) : (
                    <>
                        {/* `resolveActionVerbiage` returns '' for an unmapped
                            update, which used to open the message with a bare
                            period. */}
                        {actionDescription && <span>{actionDescription}. </span>}
                        Unselect anything you no longer want to change, then retry.{' '}
                        {selectedCount === conflictCount
                            ? 'All of them will be retried.'
                            : `${selectedCount.toLocaleString()} will be retried.`}
                    </>
                )
            }
            className="mb-[18px]"
        />
        <div className="min-w-0 overflow-x-auto rounded-[7px] border border-[#d8e0e8]">
            {children}
        </div>
    </BulkActionDialog>
)
